import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import {
  CalendarIcon,
  MapPinIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import { isTrainer, isBoard } from '../utils/roleUtils';
import ConfirmationModal from '../components/ConfirmationModal';

interface Event {
  id: number;
  title: string;
  start: string;
  start_time: string;
  end_time: string;
  beschreibung?: string;
  location?: string;
  creator_name: string;
  creator_image?: string;
  creator_image_updated?: number;
  creator: string;
  cotrainer?: string;
  cotrainer_name?: string;
  cotrainer_image?: string;
  cotrainer_image_updated?: number;
  max_teilnehmer?: number;
  participantCount: number;
  unique_id: string;
  farbe: string;
  serie: boolean;
  participants?: string[];
}

interface EventParticipant {
  mg_nr: string;
  name: string;
  vorname: string;
  email: string;
  telefon: string;
  member_type: 'member' | 'guest';
  guest_card_id?: string;
  booking_date: string;
}

interface CancellationPeriods {
  members: string | number;
  guests: number;
}

interface EventsResponse {
  eventsCreated: Event[];
  eventsBooked: Event[];
  statistics?: {
    monthly: { topUserBookings: number; yourBookings: number; yourRank: number };
    yearly: { topUserBookings: number; yourBookings: number; yourRank: number };
  };
}

interface EventGroup {
  unique_id: string;
  title: string;
  events: Event[];
  creator_name: string;
  isExpanded: boolean;
}

const CARD_BG = '#1c2432';
const HERO_PHOTO_DEFAULT =
  'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=1600&auto=format&fit=crop&q=80';

const MyBookings: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const [eventsCreated, setEventsCreated] = useState<Event[]>([]);
  const [eventsBooked, setEventsBooked] = useState<Event[]>([]);
  const [, setStatistics] = useState<EventsResponse['statistics'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);
  const [activeTab, setActiveTab] = useState<'created' | 'booked'>('booked');
  const [eventGroups, setEventGroups] = useState<Map<string, EventGroup>>(new Map());
  const [participantsData, setParticipantsData] = useState<Map<number, EventParticipant[]>>(
    new Map()
  );
  const [showParticipants, setShowParticipants] = useState<Set<number>>(new Set());
  const [loadingParticipants, setLoadingParticipants] = useState<Set<number>>(new Set());
  const [cancellationPeriods, setCancellationPeriods] = useState<CancellationPeriods | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    type: 'success' | 'warning' | 'error' | 'question' | 'info';
    title: string;
    message: string;
    variant: 'single' | 'two';
    onConfirm?: () => void;
    onClose?: () => void;
  }>({
    visible: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single',
  });

  const isTrainerUser = isTrainer(user) || isBoard(user);

  const fetchCancellationPeriods = async () => {
    try {
      const response = await apiClient.get('/api/config/cancellation_periods');
      if (response.data.success) setCancellationPeriods(response.data.data);
    } catch (error) {
      console.error('Error fetching cancellation periods:', error);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const url = showPast
        ? '/api/calendar/user-events-calendar/unknown?past=true'
        : '/api/calendar/user-events-calendar/unknown';
      const response = await apiClient.get(url);
      const data: EventsResponse = response.data;
      setEventsCreated(data.eventsCreated || []);
      setEventsBooked(data.eventsBooked || []);
      setStatistics(data.statistics || null);
      processEventGroups([...data.eventsCreated, ...data.eventsBooked]);
    } catch (error) {
      console.error('Error fetching events:', error);
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Termine konnten nicht geladen werden.',
        variant: 'single',
      });
    } finally {
      setLoading(false);
    }
  };

  const processEventGroups = (allEvents: Event[]) => {
    const groups = new Map<string, EventGroup>();
    const expandState = JSON.parse(localStorage.getItem('eventGroups_expanded') || '{}');
    allEvents.forEach((event) => {
      if (event.serie && event.unique_id) {
        if (!groups.has(event.unique_id)) {
          groups.set(event.unique_id, {
            unique_id: event.unique_id,
            title: event.title,
            events: [],
            creator_name: event.creator_name,
            isExpanded: expandState[event.unique_id] || false,
          });
        }
        groups.get(event.unique_id)!.events.push(event);
      }
    });
    setEventGroups(groups);
  };

  const toggleEventGroup = (uniqueId: string) => {
    const newGroups = new Map(eventGroups);
    const group = newGroups.get(uniqueId);
    if (group) {
      group.isExpanded = !group.isExpanded;
      newGroups.set(uniqueId, group);
      setEventGroups(newGroups);
      const expandState = JSON.parse(localStorage.getItem('eventGroups_expanded') || '{}');
      expandState[uniqueId] = group.isExpanded;
      localStorage.setItem('eventGroups_expanded', JSON.stringify(expandState));
    }
  };

  const fetchParticipants = async (eventId: number) => {
    if (participantsData.has(eventId)) return;
    setLoadingParticipants((prev) => new Set(prev).add(eventId));
    try {
      const response = await apiClient.get(`/api/calendar/${eventId}/participants`);
      if (response.data.success) {
        setParticipantsData((prev) => new Map(prev).set(eventId, response.data.participants));
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
    } finally {
      setLoadingParticipants((prev) => {
        const newSet = new Set(prev);
        newSet.delete(eventId);
        return newSet;
      });
    }
  };

  const toggleParticipants = async (eventId: number) => {
    const newShowParticipants = new Set(showParticipants);
    if (newShowParticipants.has(eventId)) {
      newShowParticipants.delete(eventId);
    } else {
      newShowParticipants.add(eventId);
      await fetchParticipants(eventId);
    }
    setShowParticipants(newShowParticipants);
  };

  const canCancelEvent = (
    event: Event,
    asTrainer: boolean = false
  ): { allowed: boolean; reason?: string } => {
    if (!cancellationPeriods)
      return { allowed: false, reason: 'Stornierungsregeln werden geladen...' };
    const eventDate = new Date(`${event.start}T${event.start_time}`);
    const now = new Date();
    const hoursDiff = (eventDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    if (hoursDiff < 0) {
      return asTrainer
        ? { allowed: true, reason: 'Event ist bereits vorbei, als Trainer stornieren?' }
        : { allowed: false, reason: 'Event ist bereits vorbei' };
    }
    const memberPeriod = cancellationPeriods.members;
    if (memberPeriod === 'unbegrenzt') return { allowed: true };
    if (typeof memberPeriod === 'number' && hoursDiff < memberPeriod) {
      return asTrainer
        ? { allowed: true, reason: 'Stornierungsfrist überschritten, als Trainer stornieren?' }
        : { allowed: false, reason: `Stornierung nur bis ${memberPeriod}h vor dem Event möglich` };
    }
    return { allowed: true };
  };

  const cancelBooking = async (eventId: number) => {
    try {
      const response = await apiClient.delete(`/api/members/cancel-event/${eventId}`);
      if (response.data.success) {
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Termin wurde erfolgreich storniert.',
          variant: 'single',
          onClose: () => fetchEvents(),
        });
        fetchEvents();
      } else {
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: response.data.message || 'Stornierung fehlgeschlagen.',
          variant: 'single',
        });
      }
    } catch (error: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: error.response?.data?.message || 'Stornierung fehlgeschlagen.',
        variant: 'single',
      });
    }
  };

  const removeParticipant = async (eventId: number, participant: EventParticipant) => {
    try {
      const cardId = participant.guest_card_id || '';
      const response = await apiClient.delete(
        `/api/members/cancel-event/${eventId}?member_id=${participant.mg_nr}&card_id=${cardId}`
      );
      if (response.data.success) {
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Teilnehmer wurde erfolgreich entfernt.',
          variant: 'single',
        });
        setParticipantsData((prev) => {
          const newMap = new Map(prev);
          newMap.delete(eventId);
          return newMap;
        });
        fetchEvents();
      } else {
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: response.data.message || 'Teilnehmer konnte nicht entfernt werden.',
          variant: 'single',
        });
      }
    } catch (error: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: error.response?.data?.message || 'Teilnehmer konnte nicht entfernt werden.',
        variant: 'single',
      });
    }
  };

  const handleCancelConfirm = (event: Event, asTrainer = false) => {
    const cancelCheck = canCancelEvent(event, asTrainer);
    if (!cancelCheck.allowed && !asTrainer) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Stornierung nicht möglich',
        message: cancelCheck.reason || 'Stornierung nicht erlaubt.',
        variant: 'single',
      });
      return;
    }
    const message = cancelCheck.reason || 'Möchten Sie diesen Termin wirklich stornieren?';
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Termin stornieren',
      message,
      variant: 'two',
      onConfirm: () => cancelBooking(event.id),
    });
  };

  const handleParticipantRemoval = (
    eventId: number,
    participant: EventParticipant,
    event: Event
  ) => {
    const cancelCheck = canCancelEvent(event, true);
    const message = cancelCheck.reason
      ? `${cancelCheck.reason} Teilnehmer: ${participant.vorname} ${participant.name}`
      : `Möchten Sie ${participant.vorname} ${participant.name} wirklich von diesem Termin entfernen?`;
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Teilnehmer entfernen',
      message,
      variant: 'two',
      onConfirm: () => removeParticipant(eventId, participant),
    });
  };

  const getUserImageUrl = (userId: string) => {
    const token = localStorage.getItem('jwt_token');
    return `${BASE_URL}/api/members/user-image-url/${userId}?size=S&token=${token}`;
  };

  const isEventPast = (event: Event) => {
    const now = new Date();
    if (event.end_time) {
      const eventEndDateTime = new Date(`${event.start}T${event.end_time}`);
      return eventEndDateTime < now;
    }
    const eventStartDateTime = new Date(`${event.start}T${event.start_time}`);
    return eventStartDateTime < now;
  };

  const formatDayShort = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '');
  };

  const formatDayNum = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.getDate().toString().padStart(2, '0');
  };

  const formatMonthShort = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '');
  };

  const formatTimeRange = (startTime: string, endTime: string) => {
    const trim = (t: string) => (t ? t.substring(0, 5) : '');
    return `${trim(startTime)} – ${trim(endTime)}`;
  };

  const getDaysUntil = (event: Event) => {
    const eventDate = new Date(`${event.start}T${event.start_time}`);
    const now = new Date();
    const diffMs = eventDate.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  const getCapacityPct = (event: Event) => {
    const max = Number(event.max_teilnehmer || 0);
    if (max <= 0) return 0;
    return Math.min(100, Math.round((event.participantCount / max) * 100));
  };

  const getCapacityColor = (pct: number) => {
    if (pct >= 90) return '#f43f5e'; // rose
    if (pct >= 75) return '#f59e0b'; // amber
    return '#34d399'; // primary
  };

  useEffect(() => {
    fetchCancellationPeriods();
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPast]);

  const currentEvents = activeTab === 'created' ? eventsCreated : eventsBooked;

  const getNextEvent = (events: Event[]) => {
    const futureEvents = events
      .filter((event) => !isEventPast(event))
      .sort((a, b) => {
        const dateA = new Date(`${a.start}T${a.start_time}`);
        const dateB = new Date(`${b.start}T${b.start_time}`);
        return dateA.getTime() - dateB.getTime();
      });
    return futureEvents[0] || null;
  };

  const nextEvent = getNextEvent(currentEvents);

  const processedEvents = () => {
    const individualEvents: Event[] = [];
    const seriesGroups: EventGroup[] = [];
    const filteredEvents = showPast
      ? currentEvents
      : currentEvents.filter((event) => !isEventPast(event));

    filteredEvents.forEach((event) => {
      if (event.serie && event.unique_id && eventGroups.has(event.unique_id)) {
        const group = eventGroups.get(event.unique_id)!;
        if (!seriesGroups.find((g) => g.unique_id === group.unique_id)) {
          const groupEvents = showPast
            ? group.events
            : group.events.filter((e) => !isEventPast(e));
          if (groupEvents.length > 0) {
            seriesGroups.push({ ...group, events: groupEvents });
          }
        }
      } else {
        individualEvents.push(event);
      }
    });

    return { individualEvents, seriesGroups };
  };

  const { individualEvents, seriesGroups } = processedEvents();

  const upcomingCount = currentEvents.filter((e) => !isEventPast(e)).length;
  const totalCount = eventsCreated.length + eventsBooked.length;

  // ----- RENDER HELPERS -----

  const ProfileAvatar: React.FC<{ participant: EventParticipant }> = ({ participant }) => {
    const [imageError, setImageError] = useState(false);
    const initials = `${participant.vorname.charAt(0)}${participant.name.charAt(0)}`.toUpperCase();
    const bg =
      participant.member_type === 'guest'
        ? 'bg-amber-500/15 border border-amber-500/30 text-amber-300'
        : 'bg-primary-500/15 border border-primary-500/30 text-primary-300';

    if (imageError) {
      return (
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold text-xs ${bg}`}
        >
          {initials}
        </div>
      );
    }
    return (
      <img
        src={getUserImageUrl(participant.mg_nr)}
        alt={`${participant.vorname} ${participant.name}`}
        className="w-9 h-9 rounded-lg object-cover"
        onError={() => setImageError(true)}
      />
    );
  };

  const DateTile: React.FC<{ event: Event; isNext?: boolean; isPast?: boolean }> = ({
    event,
    isNext,
    isPast,
  }) => {
    const tone = isPast ? 'muted' : isNext ? 'primary' : 'primary';
    const bg = isPast
      ? 'rgba(255,255,255,0.03)'
      : 'linear-gradient(180deg, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.04) 100%)';
    const border = isPast ? 'rgba(255,255,255,0.06)' : 'rgba(16,185,129,0.25)';
    const dayColor = isPast ? 'text-slate-400' : 'text-primary-300';
    return (
      <div
        className="shrink-0 flex flex-col items-center justify-center"
        style={{
          width: 64,
          height: 76,
          borderRadius: 14,
          background: bg,
          border: `1px solid ${border}`,
        }}
        data-tone={tone}
      >
        <div className={`text-[10px] uppercase tracking-widest font-bold ${dayColor}`}>
          {formatDayShort(event.start)}
        </div>
        <div
          className={`text-2xl font-black leading-none mt-0.5 ${
            isPast ? 'text-slate-300' : 'text-white'
          }`}
        >
          {formatDayNum(event.start)}
        </div>
        <div className="text-[10px] text-slate-400 mt-0.5">{formatMonthShort(event.start)}</div>
      </div>
    );
  };

  const CapacityRing: React.FC<{ event: Event }> = ({ event }) => {
    const max = Number(event.max_teilnehmer || 0);
    if (max <= 0) return null;
    const pct = getCapacityPct(event);
    const color = getCapacityColor(pct);
    const deg = (pct / 100) * 360;
    return (
      <div
        className="relative w-12 h-12 rounded-full"
        style={{
          background: `conic-gradient(from 0deg, ${color} 0deg ${deg}deg, rgba(255,255,255,0.08) ${deg}deg 360deg)`,
          padding: 3,
        }}
      >
        <div
          className="w-full h-full rounded-full flex items-center justify-center text-[11px] font-black"
          style={{
            background: CARD_BG,
            color: pct >= 90 ? '#fca5a5' : pct >= 75 ? '#fcd34d' : '#fff',
          }}
        >
          {pct}%
        </div>
      </div>
    );
  };

  const StatusPills: React.FC<{ event: Event; isNext?: boolean; isPast?: boolean }> = ({
    event,
    isNext,
    isPast,
  }) => {
    const pct = getCapacityPct(event);
    return (
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        {isPast ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-white/10 bg-slate-950/55 text-slate-400">
            Vergangen
          </span>
        ) : isNext ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-primary-500/30 bg-primary-500/10 text-primary-300">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulse" />
            Nächstes
          </span>
        ) : null}
        {event.serie && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-white/10 bg-slate-950/55 text-slate-300">
            Serie
          </span>
        )}
        {!isPast && pct >= 90 && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-rose-500/30 bg-rose-500/10 text-rose-300">
            Voll
          </span>
        )}
        {!isPast && pct >= 75 && pct < 90 && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-amber-500/30 bg-amber-500/10 text-amber-300">
            Fast voll
          </span>
        )}
      </div>
    );
  };

  const EventRow: React.FC<{
    event: Event;
    isNext?: boolean;
    eventType: 'created' | 'booked';
  }> = ({ event, isNext, eventType }) => {
    const past = isEventPast(event);
    const showParts = showParticipants.has(event.id);
    const partsLoading = loadingParticipants.has(event.id);
    const parts = participantsData.get(event.id);
    const cancelInfo = canCancelEvent(event);

    return (
      <div
        className="transition border-b border-gray-200 dark:border-slate-800"
      >
        <div
          className="px-5 sm:px-6 py-4 flex items-center gap-3 sm:gap-4 transition"
          onMouseEnter={(e) =>
            !past && (e.currentTarget.style.background = 'rgba(255,255,255,0.025)')
          }
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <DateTile event={event} isNext={isNext} isPast={past} />
          <div className={`flex-1 min-w-0 ${past ? 'opacity-60' : ''}`}>
            <StatusPills event={event} isNext={isNext} isPast={past} />
            <div className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">{event.title}</div>
            <div className="text-xs text-gray-600 dark:text-slate-400 mt-0.5 truncate flex items-center gap-1.5 flex-wrap">
              <span>{formatTimeRange(event.start_time, event.end_time)}</span>
              {event.location && (
                <>
                  <span className="text-gray-400 dark:text-slate-600">·</span>
                  <span className="inline-flex items-center gap-1">
                    <MapPinIcon className="w-3 h-3" />
                    {event.location}
                  </span>
                </>
              )}
              {event.creator_name && (
                <>
                  <span className="text-gray-400 dark:text-slate-600">·</span>
                  <span>{event.creator_name}</span>
                </>
              )}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-4 shrink-0">
            {event.max_teilnehmer ? (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Plätze</div>
                <div className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                  {event.participantCount} / {event.max_teilnehmer}
                </div>
              </div>
            ) : (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Anmeld.</div>
                <div className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                  {event.participantCount}
                </div>
              </div>
            )}
            <CapacityRing event={event} />
          </div>

          <div className="flex gap-2 shrink-0">
            {eventType === 'created' && !past && (
              <button
                onClick={() => toggleParticipants(event.id)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300"
              >
                <span className="hidden sm:inline">Teilnehmer</span>
                {showParts ? (
                  <ChevronUpIcon className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDownIcon className="w-3.5 h-3.5" />
                )}
              </button>
            )}
            {eventType === 'booked' && !past && (
              <button
                onClick={() => handleCancelConfirm(event)}
                disabled={!cancelInfo.allowed}
                className="px-3 py-2 rounded-xl text-xs font-bold transition disabled:opacity-50 disabled:cursor-not-allowed bg-rose-100 dark:bg-rose-500/10 border border-rose-300 dark:border-rose-500/30 text-rose-700 dark:text-rose-300"
                title={cancelInfo.reason}
              >
                Stornieren
              </button>
            )}
            {past && (
              <span className="hidden sm:inline-block text-[11px] text-gray-500 dark:text-slate-500">Abgehakt</span>
            )}
          </div>
        </div>

        {/* Participants section (trainer view) */}
        {showParts && eventType === 'created' && (
          <div
            className="px-5 sm:px-6 pb-4 pt-3 border-t border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-white/[0.015]"
          >
            {partsLoading && (
              <div className="text-xs text-gray-600 dark:text-slate-400 py-3 text-center">Lade Teilnehmer …</div>
            )}
            {!partsLoading && parts && parts.length === 0 && (
              <div className="text-xs text-gray-600 dark:text-slate-400 py-3 text-center">
                Noch keine Anmeldungen.
              </div>
            )}
            {!partsLoading && parts && parts.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {parts.map((p) => (
                  <div
                    key={`${p.mg_nr}-${p.guest_card_id || ''}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/5"
                  >
                    <ProfileAvatar participant={p} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-900 dark:text-white truncate">
                        {p.vorname} {p.name}
                      </div>
                      <div className="text-[11px] text-gray-600 dark:text-slate-400 truncate">
                        {p.member_type === 'guest' ? 'Gast' : 'Mitglied'}
                        {p.guest_card_id && ` · Karte ${p.guest_card_id}`}
                      </div>
                    </div>
                    <button
                      onClick={() => handleParticipantRemoval(event.id, p, event)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center transition"
                      style={{ color: '#94a3b8' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(244,63,94,0.10)';
                        e.currentTarget.style.color = '#fda4af';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#94a3b8';
                      }}
                      aria-label="Entfernen"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ----- LOADING STATE -----

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: 'transparent' }}>
        <div className="max-w-7xl mx-auto py-8 sm:py-10 space-y-6">
          <div
            className="rounded-3xl p-8 animate-pulse bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
          >
            <div className="h-8 w-48 bg-gray-200 dark:bg-slate-700/60 rounded mb-3" />
            <div className="h-4 w-72 bg-gray-200 dark:bg-slate-700/40 rounded" />
          </div>
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="rounded-2xl p-5 animate-pulse bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            >
              <div className="h-5 w-2/3 bg-gray-200 dark:bg-slate-700/60 rounded mb-2" />
              <div className="h-4 w-1/3 bg-gray-200 dark:bg-slate-700/40 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ----- MAIN RENDER -----

  const nextEventDays = nextEvent ? getDaysUntil(nextEvent) : 0;
  const nextEventCancel = nextEvent ? canCancelEvent(nextEvent) : null;

  return (
    <>
      <div
        className="min-h-screen font-sans text-gray-900 dark:text-slate-200"
        style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}
      >
        <div className="max-w-7xl mx-auto py-2 sm:py-4">
          {/* TABS + Past toggle — oben für konsistente Position bei Tab-Wechsel */}
          {(isTrainerUser || true) && (
            <section className="pt-2 pb-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                {isTrainerUser ? (
                  <div
                    className="inline-flex p-1 rounded-2xl bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    <button
                      onClick={() => setActiveTab('booked')}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
                        activeTab === 'booked'
                          ? 'bg-primary-500 text-slate-950'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      Buchungen
                      <span
                        className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-black ${
                          activeTab === 'booked'
                            ? 'bg-slate-950/30 text-slate-950'
                            : 'bg-white/10 text-white'
                        }`}
                      >
                        {eventsBooked.length}
                      </span>
                    </button>
                    <button
                      onClick={() => setActiveTab('created')}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
                        activeTab === 'created'
                          ? 'bg-primary-500 text-slate-950'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      Meine Events
                      <span
                        className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-black ${
                          activeTab === 'created'
                            ? 'bg-slate-950/30 text-slate-950'
                            : 'bg-white/10 text-white'
                        }`}
                      >
                        {eventsCreated.length}
                      </span>
                    </button>
                  </div>
                ) : (
                  <div />
                )}
                <button
                  onClick={() => setShowPast(!showPast)}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 ${
                    showPast
                      ? 'bg-gray-200 dark:bg-white/10'
                      : 'bg-gray-100 dark:bg-white/5'
                  }`}
                >
                  {showPast ? <EyeSlashIcon className="w-3.5 h-3.5" /> : <ClockIcon className="w-3.5 h-3.5" />}
                  <span className="hidden sm:inline">
                    Vergangene {showPast ? 'ausblenden' : 'anzeigen'}
                  </span>
                  <span className="sm:hidden">{showPast ? 'Aus' : 'Vergangene'}</span>
                </button>
              </div>
            </section>
          )}

          {/* INTRO (immer): Title konsistent, Snippet adaptiv */}
          {currentEvents.length === 0 && (
            <section className="pt-2 pb-4">
              <div className="text-[11px] uppercase tracking-[0.22em] text-primary-600/80 dark:text-primary-400/80 font-semibold mb-3">
                Meine Buchungen · Stand {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight">
                Meine <span className="text-primary-600 dark:text-primary-400">Buchungen.</span>
              </h1>
              <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mt-4">
                {activeTab === 'booked' ? (
                  <>Aktuell <span className="font-bold text-amber-700 dark:text-amber-300">kein Termin</span> in Sicht. Schau im <span className="font-bold text-gray-900 dark:text-white">Kalender</span> nach freien Plätzen.</>
                ) : (
                  <>Du hast noch <span className="font-bold text-amber-700 dark:text-amber-300">keine eigenen Events</span> erstellt. Lege ein neues Event an, um Mitglieder einzuladen.</>
                )}
              </p>
            </section>
          )}

          {/* INTRO: Kicker + Story + Stats */}
          {currentEvents.length > 0 && (
            <section className="pt-2 pb-6">
              <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-end">
                <div className="lg:col-span-8">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-primary-600/80 dark:text-primary-400/80 font-semibold mb-3">
                    {upcomingCount} {upcomingCount === 1 ? 'Termin' : 'Termine'} kommen ·{' '}
                    Stand{' '}
                    {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
                  </div>
                  <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight mb-4">
                    Meine <span className="text-primary-600 dark:text-primary-400">Buchungen.</span>
                  </h1>
                  <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
                    {nextEvent ? (
                      <>
                        Dein nächstes Training ist{' '}
                        <span className="font-bold text-primary-700 dark:text-primary-300">{nextEvent.title}</span>{' '}
                        {(() => {
                          const days = getDaysUntil(nextEvent);
                          if (days === 0)
                            return (
                              <>
                                — <span className="font-bold text-gray-900 dark:text-white">heute</span>
                              </>
                            );
                          if (days === 1)
                            return (
                              <>
                                — <span className="font-bold text-gray-900 dark:text-white">morgen</span>
                              </>
                            );
                          return (
                            <>
                              in <span className="font-bold text-gray-900 dark:text-white">{days} Tagen</span>
                            </>
                          );
                        })()}
                        {nextEvent.start_time && (
                          <>
                            {' '}um{' '}
                            <span className="font-bold text-gray-900 dark:text-white">
                              {nextEvent.start_time.substring(0, 5)} Uhr
                            </span>
                          </>
                        )}
                        {nextEvent.location && (
                          <>
                            {' '}·{' '}
                            <span className="font-bold text-gray-900 dark:text-white">{nextEvent.location}</span>
                          </>
                        )}
                        . Insgesamt{' '}
                        <span className="font-bold text-gray-900 dark:text-white">
                          {eventsBooked.length} gebucht
                        </span>
                        {isTrainerUser && eventsCreated.length > 0 && (
                          <>
                            ,{' '}
                            <span className="font-bold text-gray-900 dark:text-white">
                              {eventsCreated.length} eigene Events
                            </span>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        Aktuell <span className="font-bold text-amber-700 dark:text-amber-300">kein Termin</span> in
                        Sicht. Schau in den Kalender für freie Plätze
                      </>
                    )}
                    .
                  </p>
                </div>
                <div className="lg:col-span-4 grid grid-cols-3 gap-3">
                  <div
                    className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                      Gebucht
                    </div>
                    <div className="text-2xl lg:text-3xl font-black text-gray-900 dark:text-white mt-1">{eventsBooked.length}</div>
                  </div>
                  <div
                    className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                      Kommend
                    </div>
                    <div className="text-2xl lg:text-3xl font-black text-primary-600 dark:text-primary-400 mt-1">
                      {upcomingCount}
                    </div>
                  </div>
                  <div
                    className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                      {isTrainerUser ? 'Eigene' : 'Gesamt'}
                    </div>
                    <div className="text-2xl lg:text-3xl font-black text-gray-900 dark:text-white mt-1">
                      {isTrainerUser
                        ? eventsCreated.length
                        : eventsBooked.length + eventsCreated.length}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* HERO — Next Event */}
          {nextEvent && (
            <section className="pb-6">
              <article
                className="group relative rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{
                  boxShadow:
                    '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)',
                }}
              >
                {/* Top meta pills (overlay on photo) */}
                <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-5 sm:px-7 pt-5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-primary-500/40 bg-primary-500/15 text-primary-200 backdrop-blur">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulse" />
                      Nächste Buchung
                    </span>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-white/15 bg-slate-950/55 text-slate-200 backdrop-blur">
                      {nextEventDays === 0
                        ? 'Heute'
                        : nextEventDays === 1
                        ? 'Morgen'
                        : `In ${nextEventDays} Tagen`}
                    </span>
                    {nextEvent.serie && (
                      <span className="hidden sm:inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-white/15 bg-slate-950/55 text-slate-200 backdrop-blur">
                        Serie
                      </span>
                    )}
                  </div>
                  <div className="hidden sm:flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-[9px] uppercase tracking-widest text-slate-300/90 font-bold">
                        Gebucht
                      </div>
                      <div className="text-sm font-black text-white tabular-nums">
                        {eventsBooked.length}
                      </div>
                    </div>
                    <div className="w-px h-8 bg-white/15" />
                    <div className="text-right">
                      <div className="text-[9px] uppercase tracking-widest text-primary-300 font-bold">
                        Kommend
                      </div>
                      <div className="text-sm font-black text-primary-300 tabular-nums">
                        {upcomingCount}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hero photo with title overlay */}
                <div className="relative w-full overflow-hidden aspect-[16/10] md:aspect-[16/6]">
                  <img
                    src={HERO_PHOTO_DEFAULT}
                    alt="Training"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{ objectPosition: 'center 30%' }}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(2,6,23,0.05) 0%, rgba(2,6,23,0) 30%, rgba(2,6,23,0.55) 65%, rgba(28,36,50,0.98) 100%), radial-gradient(ellipse at top right, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 60%)',
                    }}
                  />

                  {/* Title block — overlaid at bottom of photo */}
                  <div className="absolute inset-x-0 bottom-0 px-5 sm:px-8 pb-6 sm:pb-7 z-10">
                    <div className="flex items-end justify-between gap-4 sm:gap-6">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          {nextEvent.location && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-white/20 bg-white/10 text-white backdrop-blur">
                              {nextEvent.location}
                            </span>
                          )}
                          {nextEvent.creator_name && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-primary-400/30 bg-primary-500/15 text-primary-300 backdrop-blur">
                              Trainer · {nextEvent.creator_name}
                            </span>
                          )}
                        </div>
                        <h3
                          className="text-white font-black leading-none"
                          style={{
                            fontSize: 'clamp(2rem, 5vw, 3.75rem)',
                            letterSpacing: '-0.04em',
                            textShadow:
                              '0 1px 2px rgba(0,0,0,0.55), 0 4px 18px rgba(0,0,0,0.55)',
                          }}
                        >
                          {nextEvent.title}
                        </h3>
                        <p
                          className="text-white/95 text-sm md:text-lg leading-relaxed mt-3 max-w-xl"
                          style={{
                            textShadow: '0 1px 2px rgba(0,0,0,0.55), 0 4px 18px rgba(0,0,0,0.55)',
                          }}
                        >
                          {formatDayShort(nextEvent.start)} ·{' '}
                          {formatDayNum(nextEvent.start)}.{' '}
                          {formatMonthShort(nextEvent.start)} ·{' '}
                          <span className="font-bold text-primary-300">
                            {formatTimeRange(nextEvent.start_time, nextEvent.end_time)}
                          </span>
                        </p>
                      </div>

                      {/* Capacity ring overlaid on photo */}
                      {nextEvent.max_teilnehmer ? (
                        <div className="hidden md:block shrink-0">
                          <div
                            className="relative w-28 h-28 rounded-full"
                            style={{
                              background: `conic-gradient(from 0deg, ${getCapacityColor(
                                getCapacityPct(nextEvent)
                              )} 0deg ${(getCapacityPct(nextEvent) / 100) * 360}deg, rgba(255,255,255,0.08) ${
                                (getCapacityPct(nextEvent) / 100) * 360
                              }deg 360deg)`,
                              padding: 5,
                            }}
                          >
                            <div
                              className="w-full h-full rounded-full flex flex-col items-center justify-center"
                              style={{ background: CARD_BG, border: '1px solid #1e293b' }}
                            >
                              <div className="text-[9px] uppercase tracking-widest text-slate-400">
                                Belegt
                              </div>
                              <div className="text-2xl font-black text-white leading-none">
                                {nextEvent.participantCount}/{nextEvent.max_teilnehmer}
                              </div>
                              <div className="text-[10px] text-primary-400 font-semibold mt-0.5">
                                {getCapacityPct(nextEvent)} %
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* Footer breakdown — Mobile */}
                <div
                  className="md:hidden relative px-5 py-5 border-t border-gray-200 dark:border-slate-800"
                >
                  <div className="flex items-end justify-between gap-3 mb-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                        Teilnehmer
                      </div>
                      <div className="text-lg font-black text-gray-900 dark:text-white tabular-nums">
                        {nextEvent.participantCount}
                        {nextEvent.max_teilnehmer && (
                          <span className="text-gray-600 dark:text-slate-400 font-bold"> / {nextEvent.max_teilnehmer}</span>
                        )}
                      </div>
                    </div>
                    {nextEventCancel?.allowed && (
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                          Stornierbar
                        </div>
                        <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
                          {typeof cancellationPeriods?.members === 'number'
                            ? `${cancellationPeriods.members}h vorher`
                            : 'Jederzeit'}
                        </div>
                      </div>
                    )}
                  </div>
                  {activeTab === 'booked' && (
                    <button
                      onClick={() => handleCancelConfirm(nextEvent)}
                      disabled={!nextEventCancel?.allowed}
                      className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl text-sm font-bold tracking-wide transition disabled:opacity-50 disabled:cursor-not-allowed bg-rose-100 dark:bg-rose-500/10 border border-rose-300 dark:border-rose-500/30 text-rose-700 dark:text-rose-300"
                    >
                      Stornieren
                    </button>
                  )}
                </div>

                {/* Footer breakdown — Desktop */}
                <div
                  className="hidden md:block relative px-8 py-6 border-t border-gray-200 dark:border-slate-800"
                >
                  <div className="grid grid-cols-5 gap-6 items-center">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                        Datum
                      </div>
                      <div className="text-sm font-bold text-gray-900 dark:text-white mt-1 leading-tight">
                        {formatDayShort(nextEvent.start)} ·{' '}
                        {formatDayNum(nextEvent.start)}. {formatMonthShort(nextEvent.start)}
                        <br />
                        <span className="text-[11px] text-gray-600 dark:text-slate-400 font-medium">
                          {formatTimeRange(nextEvent.start_time, nextEvent.end_time)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                        Trainer
                      </div>
                      <div className="text-sm font-bold text-gray-900 dark:text-white mt-1">{nextEvent.creator_name}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                        Teilnehmer
                      </div>
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-2xl font-black text-gray-900 dark:text-white tabular-nums">
                          {nextEvent.participantCount}
                        </span>
                        {nextEvent.max_teilnehmer && (
                          <span className="text-[11px] text-gray-600 dark:text-slate-400">
                            / {nextEvent.max_teilnehmer}
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                        Stornierbar
                      </div>
                      <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                        {nextEventCancel?.allowed
                          ? typeof cancellationPeriods?.members === 'number'
                            ? `bis ${cancellationPeriods.members}h vorher`
                            : 'Jederzeit'
                          : '—'}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {activeTab === 'booked' ? (
                        <button
                          onClick={() => handleCancelConfirm(nextEvent)}
                          disabled={!nextEventCancel?.allowed}
                          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl text-xs font-bold transition disabled:opacity-50 disabled:cursor-not-allowed bg-rose-100 dark:bg-rose-500/10 border border-rose-300 dark:border-rose-500/30 text-rose-700 dark:text-rose-300"
                        >
                          Stornieren
                        </button>
                      ) : (
                        <button
                          onClick={() => toggleParticipants(nextEvent.id)}
                          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl text-xs font-bold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300"
                        >
                          Teilnehmer
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            </section>
          )}

          {/* STATS — only when no hero (no upcoming events) */}
          {!nextEvent && currentEvents.length > 0 && (
            <section className="pt-2 pb-4">
              <div className="grid grid-cols-3 gap-3 max-w-md ml-auto">
                <div
                  className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                >
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Gebucht</div>
                  <div className="text-2xl lg:text-3xl font-black text-gray-900 dark:text-white mt-1">{eventsBooked.length}</div>
                </div>
                <div
                  className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                >
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Erstellt</div>
                  <div className="text-2xl lg:text-3xl font-black text-gray-900 dark:text-white mt-1">{eventsCreated.length}</div>
                </div>
                <div
                  className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                >
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Gesamt</div>
                  <div className="text-2xl lg:text-3xl font-black text-primary-600 dark:text-primary-400 mt-1">{totalCount}</div>
                </div>
              </div>
            </section>
          )}

          {/* EVENT LIST */}
          <section className="pb-12">
            {currentEvents.length === 0 ? (
              <div
                className="rounded-3xl p-10 sm:p-14 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{
                  boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
                }}
              >
                <div
                  className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5 bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/30"
                >
                  <CalendarIcon className="h-10 w-10 text-primary-600 dark:text-primary-400" />
                </div>
                <h4 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">
                  Noch keine Buchung.
                </h4>
                <p className="text-gray-600 dark:text-slate-400 text-base mt-3 max-w-md mx-auto">
                  {activeTab === 'booked'
                    ? 'Schau im Kalender nach freien Plätzen — dein nächstes Training wartet.'
                    : 'Du hast noch keine Events erstellt.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Series Groups */}
                {seriesGroups.map((group) => (
                  <div
                    key={group.unique_id}
                    className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                    style={{
                      boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
                    }}
                  >
                    <button
                      onClick={() => toggleEventGroup(group.unique_id)}
                      className="w-full flex items-center gap-4 px-5 sm:px-6 py-4 transition"
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')
                      }
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div
                        className="w-1.5 h-12 rounded-full shrink-0"
                        style={{ background: group.events[0]?.farbe || '#10b981' }}
                      />
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h5 className="text-base sm:text-lg font-black text-gray-900 dark:text-white truncate">
                            {group.title}
                          </h5>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-primary-500/30 bg-primary-500/10 text-primary-700 dark:text-primary-300">
                            Serie · {group.events.length}{' '}
                            {group.events.length === 1 ? 'Termin' : 'Termine'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 dark:text-slate-400 mt-0.5 truncate">
                          {group.creator_name}
                        </div>
                      </div>
                      <ChevronDownIcon
                        className={`w-5 h-5 text-gray-600 dark:text-slate-400 transition-transform shrink-0 ${
                          group.isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {group.isExpanded && (
                      <div className="border-t border-gray-200 dark:border-slate-800">
                        {group.events
                          .sort(
                            (a, b) =>
                              new Date(`${a.start}T${a.start_time}`).getTime() -
                              new Date(`${b.start}T${b.start_time}`).getTime()
                          )
                          .map((event) => (
                            <EventRow
                              key={event.id}
                              event={event}
                              isNext={nextEvent?.id === event.id}
                              eventType={activeTab}
                            />
                          ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Individual Events */}
                {individualEvents.length > 0 && (
                  <div
                    className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                    style={{
                      boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
                    }}
                  >
                    <div className="divide-y divide-gray-200 dark:divide-slate-800">
                      {individualEvents
                        .sort(
                          (a, b) =>
                            new Date(`${a.start}T${a.start_time}`).getTime() -
                            new Date(`${b.start}T${b.start_time}`).getTime()
                        )
                        .map((event) => (
                          <EventRow
                            key={event.id}
                            event={event}
                            isNext={nextEvent?.id === event.id}
                            eventType={activeTab}
                          />
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </div>

      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal((prev) => ({ ...prev, visible: false }))}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        buttons={
          confirmModal.variant === 'two'
            ? {
                cancel: {
                  text: 'Abbrechen',
                  onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })),
                },
                confirm: {
                  text: 'Bestätigen',
                  onPress: () => {
                    confirmModal.onConfirm?.();
                    setConfirmModal((prev) => ({ ...prev, visible: false }));
                  },
                },
              }
            : undefined
        }
        button={
          confirmModal.variant === 'single'
            ? {
                text: 'OK',
                onPress: () => {
                  confirmModal.onClose?.();
                  setConfirmModal((prev) => ({ ...prev, visible: false }));
                },
              }
            : undefined
        }
      />
    </>
  );
};

export default MyBookings;
